"use client";

import { CreateWorkplaceRequestShareLinkParams } from "@gettactic/api";
import { getApiErrorMessage } from "@gettactic/helpers/src/errors/getApiErrorMessage";
import { useInvalidateWorkplaceRequestShareLinks } from "@gettactic/hooks";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { api } from "@/lib/api/api";

/**
 * Create and revoke guest share links for a workplace request. Both mutations
 * invalidate the share-link list so the sharing UI stays in sync. Requires the
 * request's `capabilities.can_share`.
 */
export function useWorkplaceRequestShareLinkMutations() {
  const invalidate = useInvalidateWorkplaceRequestShareLinks();

  const createMutation = useMutation({
    mutationFn: async ({
      ticketId,
      params
    }: {
      ticketId: string;
      params: CreateWorkplaceRequestShareLinkParams;
    }) => {
      const res = await api.client.workplaceRequests.createShareLink(
        ticketId,
        params
      );
      return res.result;
    },
    onSuccess: async () => {
      await invalidate();
      toast.success("Share link created");
    },
    onError: (error) =>
      toast.error(getApiErrorMessage(error, "Failed to create share link"))
  });

  const deleteMutation = useMutation({
    mutationFn: ({ ticketId, linkId }: { ticketId: string; linkId: string }) =>
      api.client.workplaceRequests.deleteShareLink(ticketId, linkId),
    onSuccess: async () => {
      await invalidate();
      toast.success("Share link revoked");
    },
    onError: (error) =>
      toast.error(getApiErrorMessage(error, "Failed to revoke share link"))
  });

  return { createMutation, deleteMutation };
}
